/**
 * @fileoverview Document processing status polling.
 *
 * Polls the CAVE API for document processing status with automatic retry logic.
 * Handles transient errors (5xx, 408, 429), terminal statuses (completed,
 * completed_with_errors, failed), and enforces a global deadline to prevent
 * indefinite polling.
 */

import { apiRequest } from 'platform/utilities/api';
import { buildStatusUrl } from './endpoints';
import {
  createCaveError,
  isTerminalScanStatus,
  isUnknownScanStatus,
} from './errors';
import { createThrowIfAborted } from './abort';
import { withRetry, assertGlobalDeadline } from './withRetry';

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const DEFAULT_OPTIONS = {
  intervalMs: 2000,
};

/**
 * Polls the CAVE API for document processing status until completion or deadline.
 *
 * Automatically retries on transient HTTP errors (5xx, 408, 429) and continues
 * polling until a terminal status is reached (completed, completed_with_errors,
 * failed) or the global deadline is exceeded. Unrecognized statuses fail fast.
 *
 * @async
 * @param {string} documentId - The ID of the document being processed
 * @param {object} [options={}] - Polling configuration
 * @param {number} [options.intervalMs=2000] - Milliseconds between polls
 * @param {number} globalDeadline - Absolute timestamp (ms) marking the deadline
 * @param {function} [shouldAbort] - Function that returns true if polling should abort
 * @returns {Promise<object>} The status payload with scanStatus and error details
 * @throws {Error} If global deadline is missing, exceeded, or on non-retriable errors
 */
export const pollDocumentStatus = async (
  documentId,
  options = {},
  globalDeadline,
  shouldAbort,
) => {
  const { intervalMs } = { ...DEFAULT_OPTIONS, ...options };

  assertGlobalDeadline(globalDeadline, 'status polling');

  const deadline = globalDeadline;
  const throwIfAborted = createThrowIfAborted(shouldAbort);

  // Poll until the scan reaches a terminal state. Transient request errors are
  // retried by withRetry; a successful-but-pending response loops here.
  while (Date.now() <= deadline) {
    // eslint-disable-next-line no-await-in-loop
    const payload = await withRetry(
      () => apiRequest(buildStatusUrl(documentId)),
      {
        globalDeadline: deadline,
        shouldAbort,
        intervalMs,
        errorPrefix: 'CAVE status request failed',
        fallbackDetail: 'Unable to retrieve document processing status.',
        operationName: 'status polling',
      },
    );

    // Shape guards are non-retriable — a malformed payload shape is not a transient error.
    if (payload === null || typeof payload !== 'object') {
      throw createCaveError(new Error('Response is not an object'), {
        prefix: 'CAVE status: invalid response shape',
        fallbackDetail: 'Unable to retrieve document processing status.',
      });
    }

    if (typeof payload.scanStatus !== 'string') {
      throw createCaveError(new Error('Missing scanStatus in response'), {
        prefix: 'CAVE status: missing scanStatus',
        fallbackDetail: 'Unable to retrieve document processing status.',
      });
    }

    // Exit polling on ANY terminal status (completed, completed_with_errors,
    // failed). Callers inspect scanStatus to branch on the specific outcome.
    if (isTerminalScanStatus(payload.scanStatus)) {
      return payload;
    }

    // An unrecognized scan status is non-retriable: fail fast with a clear
    // message instead of polling until the global deadline.
    if (isUnknownScanStatus(payload.scanStatus)) {
      throw createCaveError(
        new Error(`Unknown scan status: ${payload.scanStatus}`),
        {
          prefix: 'CAVE status: unrecognized scan status',
          fallbackDetail: `The document processing service returned an unrecognized status ("${payload.scanStatus}"). Please try again.`,
        },
      );
    }

    throwIfAborted();

    // eslint-disable-next-line no-await-in-loop
    await sleep(Math.min(intervalMs, deadline - Date.now()));
  }

  // Timeout-specific messaging: name the phase (status polling) so the failure
  // is diagnosable, and explain the likely cause (server was slow to finish).
  throw new Error(
    'Timed out while waiting for document processing to start. ' +
      'The processing service did not finish in time — it may be experiencing ' +
      'delays. Try uploading again.',
  );
};

export default pollDocumentStatus;
